import React from 'react';

import { call, put, all, takeLatest, select } from '@redux-saga/core/effects';
import CryptoES from 'crypto-es';
import { FormattedMessage } from 'react-intl';

import { inviteUserRequest } from '@api/inviteUser';
import { verifyInvitationRequest } from '@api/signUp';
import { keysToSnake } from '@helpers/camelCaseConverter';
import { invitedUserModel } from '@models/User';
import { verifyOtpSaga } from '@sagas/auth/verifyOtpSaga';
import {
  signUpSuccessAction,
  signUpFailureAction,
  verifyInviteTokenFailure,
  verifyInviteTokenSuccess,
  resendOTPSuccessAction,
  resendOTPFailureAction,
} from '@store/inviteUser/signUp/signUp.actions';
import {
  SIGNUP_REQUEST,
  VERIFY_OTP_REQUEST,
  RESEND_OTP_REQUEST,
  VERIFY_INVITE_TOKEN,
} from '@store/inviteUser/signUp/signUp.const';
import { toastError, toastSuccess } from '@store/notifications';
import {
  invitedUserSelector,
  invitedUserTokenSelector,
  invitedUserPasswordSelector,
} from '@store/selectors/inviteUser';

export const handleVerifyInvite = function* ({ payload }) {
  try {
    const response = yield call(verifyInvitationRequest, payload.token);
    if (!response.userInviting) return yield put(verifyInviteTokenFailure(response));
    yield put(verifyInviteTokenSuccess(response));
  } catch (errors) {
    const { nonFieldErrors } = errors?.data || {};
    yield put(
      verifyInviteTokenFailure(
        nonFieldErrors || (
          <FormattedMessage
            defaultMessage="An error occurred while retrieving your invitation. Please try again."
            id="components.invite-user-form.retrieve-invite-failed"
          />
        )
      )
    );
  }
};

export const handleResendTwoFactorAuthCodeRequest = function* ({ payload }) {
  try {
    const { phoneNumber, phonePrefix } = payload;

    const user = yield select(invitedUserSelector);
    const token = yield select(invitedUserTokenSelector);
    const password = yield select(invitedUserPasswordSelector);

    let passwordHash = CryptoES.SHA512(password);
    passwordHash = passwordHash.toString(CryptoES.enc.Hex);

    const userData = { ...user, phoneNumber, phonePrefix, token, password: passwordHash };

    yield call(inviteUserRequest, keysToSnake(userData));

    yield put([
      toastSuccess({
        message: (
          <FormattedMessage defaultMessage="OTP resent." id="global.notification.resent-otp" />
        ),
      }),
      resendOTPSuccessAction(),
    ]);
  } catch (errors) {
    const { nonFieldErrors } = errors?.data || {};

    yield put([
      toastError({
        message: nonFieldErrors || (
          <FormattedMessage
            defaultMessage="An unexpected error occurred, please try again."
            id="global.notification.api-error-default"
          />
        ),
      }),
      resendOTPFailureAction(),
    ]);
  }
};

export const handleSignUpUser = function* ({ payload }) {
  const { user } = payload || {};
  const updatedUser = { ...user };

  try {
    let passwordHash = CryptoES.SHA512(updatedUser.password);
    passwordHash = passwordHash.toString(CryptoES.enc.Hex);
    updatedUser.password = passwordHash;

    const response = yield call(inviteUserRequest, keysToSnake(updatedUser));

    const userData = invitedUserModel({
      ...updatedUser,
      ...response,
      mobile: response?.phoneNumber,
    });

    yield put(signUpSuccessAction({ user: userData }));
    yield call(payload.onSuccess);
  } catch (errors) {
    const { nonFieldErrors, hasFieldError, fieldErrors } = errors?.data || {};
    if (hasFieldError) {
      yield call(payload.setErrors, fieldErrors);
    }

    yield put(
      toastError({
        message: nonFieldErrors || (
          <FormattedMessage
            defaultMessage="Failed to save your user details."
            id="components.invite-user-form.save-failed"
          />
        ),
      })
    );
    const userData = invitedUserModel(updatedUser);
    yield put(signUpFailureAction({ user: userData }));

    yield call(payload.setSubmitting, false);
  }
};

export default all([
  takeLatest(RESEND_OTP_REQUEST, handleResendTwoFactorAuthCodeRequest),
  takeLatest(VERIFY_OTP_REQUEST, verifyOtpSaga),
  takeLatest(SIGNUP_REQUEST, handleSignUpUser),
  takeLatest(VERIFY_INVITE_TOKEN, handleVerifyInvite),
]);
